import { Box, VStack } from "@chakra-ui/react";
import { ReactNode } from "react";
import { createPortal } from "react-dom";
import { PurchaseButton } from "../components/PurchaseButton";
import { STRIPE_FORM_ID } from "../components/StripePayment";
import { useIsInsideFunnelModal } from "../../funnel/funnelModalContext";

/**
 * Renders a Subscribe CTA pinned to the bottom of the visible paywall area.
 *
 * Placement strategy (auto):
 *   - If a modal/slide-in portal target exists (`gcModalContentContainer` or
 *     `gcSlideInMenuContentContainer`) we portal into it so the CTA sticks
 *     to the bottom of the modal viewport regardless of scroll position.
 *   - Otherwise we fall back to `position: sticky; bottom: 0` so the CTA
 *     pins to the visible bottom of its containing block while the user
 *     scrolls. This is the path used by page-level paywalls.
 *
 * When `formId` is provided and `onClick` is omitted, the default click
 * handler submits that form via `form.requestSubmit()` — letting a parent
 * pair this external CTA with an embedded Stripe form whose internal
 * submit button is hidden.
 */
export function StickyPurchaseButton({
  isDisabled,
  loading,
  text,
  onClick,
  formId = STRIPE_FORM_ID,
  isInlineEmbed = false,
  children,
}: {
  isDisabled: boolean;
  loading: boolean;
  text: string;
  onClick?: () => void;
  formId?: string;
  /** When true, renders the button plainly inline without chrome — for
   *  contexts where the parent already provides the surrounding container. */
  isInlineEmbed?: boolean;
  /** Optional content rendered directly above the button inside the same
   *  bottom sheet (typically the embedded Stripe form). Bundled into the
   *  same portal call so JSX order = DOM order — otherwise the form,
   *  which mounts later than the always-rendered button, would land
   *  *below* the button in the portal target. */
  children?: ReactNode;
}) {
  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    const form = document.getElementById(formId);
    if (form instanceof HTMLFormElement) {
      form.requestSubmit();
    }
  };

  const button = (
    <PurchaseButton
      isDisabled={isDisabled}
      text={text}
      loading={loading}
      onClick={handleClick}
    />
  );

  if (isInlineEmbed) {
    return (
      <Box pt="8px" pointerEvents="auto" flexShrink={0}>
        {children ? (
          <VStack gap={3} alignItems="stretch">
            {children}
            {button}
          </VStack>
        ) : (
          button
        )}
      </Box>
    );
  }

  const chrome = (
    <Box
      p="24px"
      borderTop="1px solid"
      borderColor="transparent.white.20"
      background="charcoal.charcoal_95"
      flexShrink={0}
      pointerEvents="auto"
    >
      {children ? (
        <VStack gap={3} alignItems="stretch">
          {children}
          {button}
        </VStack>
      ) : (
        button
      )}
    </Box>
  );

  // Only portal when we're rendered inside a FunnelModal — otherwise the
  // page-level CTA bleeds into whatever unrelated modal happens to be open
  // (a /gold Subscribe button portalling into a claim-username modal, for
  // example).
  const isInsideFunnelModal = useIsInsideFunnelModal();
  const portalTarget =
    isInsideFunnelModal && typeof document !== "undefined"
      ? (document.getElementById("gcModalContentContainer") ??
        document.getElementById("gcSlideInMenuContentContainer"))
      : null;
  if (portalTarget) {
    return createPortal(chrome, portalTarget);
  }

  return (
    <Box position="sticky" bottom={0} zIndex={2} width="100%">
      {chrome}
    </Box>
  );
}
